import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { TrendingUp, TrendingDown, Home, LogOut } from 'lucide-react';
import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from './firebase';
import { apiFetch } from './apiClient';
import { getStockData, stockSkeleton } from './data/stockData';

const normalizeTreeToSectors = (tree = []) => {
  return tree.map((sector) => ({
    id: sector.id,
    name: sector.name,
    industries: (sector.children || []).map((industry) => ({
      id: industry.id,
      name: industry.name,
      stocks: (industry.children || []).map((stock) => ({
        id: stock.id,
        name: (stock.ticker || stock.name || '').toUpperCase(),
        ticker: stock.ticker,
        price: Number(stock.price ?? 0),
        change: Number(stock.change ?? 0),
        marketCap: Number(stock.marketCap ?? stock.value ?? 0)
      }))
    }))
  }));
};

const buildStockLookup = (sectors = []) => {
  const lookup = new Map();
  sectors.forEach((sector) => {
    (sector.industries || []).forEach((industry) => {
      (industry.stocks || []).forEach((stock) => {
        if (stock?.name) {
          lookup.set(stock.name.toUpperCase(), stock);
        }
        if (stock?.ticker) {
          lookup.set(stock.ticker.toUpperCase(), stock);
        }
      });
    });
  });
  return lookup;
};

const clampChange = (change) => {
  const numeric = Number.isFinite(change) ? change : 0;
  return Math.max(-10, Math.min(10, numeric));
};

const getChangeColor = (change) => {
  const clamped = clampChange(change);
  const intensity = Math.pow(Math.abs(clamped) / 10, 0.55); // keep mid-tones richer
  if (clamped >= 0) {
    return d3.interpolateRgb('#4ade80', '#064e3b')(intensity); // vibrant to deep green
  }
  return d3.interpolateRgb('#f87171', '#7f1d1d')(intensity); // vibrant to deep red
};

const getNodeMarketCap = (node, type) => {
  if (type === 'stock') {
    return node.marketCap || 0;
  }

  if (type === 'industry') {
    const stocks = node.stocks || node.children || [];
    return stocks.reduce((sum, stock) => sum + (stock.marketCap || 0), 0);
  }

  const industries = node.industries || node.children || [];
  return industries.reduce((sectorSum, industry) => {
    const stocks = industry.stocks || industry.children || [];
    return sectorSum + stocks.reduce((sum, stock) => sum + (stock.marketCap || 0), 0);
  }, 0);
};

const computeAggregateChange = (node, type) => {
  if (type === 'stock') {
    return Number.isFinite(node.change) ? node.change : 0;
  }

  const stocks = [];
  if (type === 'industry') {
    (node.stocks || node.children || []).forEach((s) => stocks.push(s));
  } else {
    (node.industries || node.children || []).forEach((industry) => {
      (industry.stocks || industry.children || []).forEach((s) => stocks.push(s));
    });
  }

  if (!stocks.length) return 0;

  const totalCap = stocks.reduce((sum, stock) => sum + (stock.marketCap || 0), 0);
  if (totalCap > 0) {
    const weighted = stocks.reduce(
      (sum, stock) => sum + (stock.change || 0) * (stock.marketCap || 0),
      0
    );
    return weighted / totalCap;
  }

  const avg = stocks.reduce((sum, stock) => sum + (stock.change || 0), 0) / stocks.length;
  return Number.isFinite(avg) ? avg : 0;
};

const createBubbleSizer = (marketCaps, type) => {
  const totalCap = marketCaps.reduce((sum, cap) => sum + cap, 0) || 1;
  const minRadius = type === 'stock' ? 55 : type === 'industry' ? 80 : 110;
  const maxRadius = type === 'stock' ? 140 : type === 'industry' ? 180 : 220;

  // Scale by area so visual sizes track with market cap share of the current level
  const minArea = Math.PI * minRadius * minRadius;
  const maxArea = Math.PI * maxRadius * maxRadius;

  return (cap) => {
    const share = cap / totalCap;
    const area = minArea + share * (maxArea - minArea);
    return Math.sqrt(area / Math.PI);
  };
};

const BubbleChart = ({ onNavigate, user, onLogout }) => {
  const svgRef = useRef(null);
  const [tooltip, setTooltip] = useState({
    visible: false,
    x: 0,
    y: 0,
    data: null
  });
  const [currentLevel, setCurrentLevel] = useState('sector');
  const [selectedSector, setSelectedSector] = useState(null);
  const [selectedIndustry, setSelectedIndustry] = useState(null);
  const [breadcrumb, setBreadcrumb] = useState(['Stock Market']);
  const [portfolio, setPortfolio] = useState([]);
  const [exportStatus, setExportStatus] = useState('');
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [dataStatus, setDataStatus] = useState('');
  const [hasLoadedLiveData, setHasLoadedLiveData] = useState(false);
  const userInitial = user?.displayName?.[0]?.toUpperCase() || 'N';
  const hasLoadedFromDb = useRef(false);

  const [sectorData, setSectorData] = useState(normalizeTreeToSectors(stockSkeleton));
  const isInteractive = hasLoadedLiveData && !isLoadingData;
  const loadMarketData = useCallback(async (forceRefresh = false) => {
    setIsLoadingData(true);
    setDataStatus(forceRefresh ? 'Refreshing live market data...' : 'Loading live market data...');
    try {
      const liveTree = await getStockData(forceRefresh);
      const normalized = normalizeTreeToSectors(liveTree);
      setSectorData(normalized);
      const stockLookup = buildStockLookup(normalized);
      setPortfolio((prev) =>
        prev.map((stock) => {
          const key = (stock.ticker || stock.name || '').toUpperCase();
          const updated = stockLookup.get(key);
          if (!updated) return stock;
          return {
            ...stock,
            name: updated.name || stock.name,
            ticker: updated.ticker || stock.ticker,
            price: updated.price,
            change: updated.change,
            marketCap: updated.marketCap
          };
        })
      );
      setCurrentLevel('sector');
      setSelectedSector(null);
      setSelectedIndustry(null);
      setBreadcrumb(['Stock Market']);
      setDataStatus('Live market data loaded');
    } catch (err) {
      console.warn('Live market data unavailable, keeping fallback.', err);
      setDataStatus('Live data unavailable. Showing fallback data.');
    } finally {
      setHasLoadedLiveData(true);
      setTimeout(() => setDataStatus(''), 3000);
      setIsLoadingData(false);
    }
  }, []);

  const addToPortfolio = useCallback((stock) => {
      setPortfolio(prev => {
        const existingStock = prev.find(s => s.name === stock.name);
        if (existingStock) {
          return prev.map(s =>
            s.name === stock.name
            ? { ...s, quantity: s.quantity + 1 }
            : s
        );
      } else {
        const portfolioStock = {
          ...stock,
          id: `${stock.name}-${Date.now()}`,
          quantity: 1,
          addedAt: new Date()
        };
        return [...prev, portfolioStock];
      }
    });
  }, []);

  const drawBubbles = useCallback((dataArray, type) => {
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const width = 800;
    const height = 600;

    svg.attr('width', width).attr('height', height);

    const centerX = width / 2;
    const centerY = height / 2;

    const marketCaps = dataArray.map(item => getNodeMarketCap(item, type));
    const sizeForCap = createBubbleSizer(marketCaps, type);

    dataArray.forEach((d, i) => {
      d.totalMarketCap = marketCaps[i];
      d.radius = sizeForCap(marketCaps[i]);
      d.aggregateChange = computeAggregateChange(d, type);
    });

    const maxBubbleSize = Math.max(...dataArray.map(d => d.radius));
    const circleRadius = maxBubbleSize * 0.8;
    
    dataArray.forEach((d, i) => {
      const angle = (i / dataArray.length) * 2 * Math.PI;
      d.x = centerX + Math.cos(angle) * circleRadius;
      d.y = centerY + Math.sin(angle) * circleRadius;
    });

    const simulation = d3.forceSimulation(dataArray)
      .force('collision', d3.forceCollide()
        .radius(d => d.radius + 2)
        .iterations(4)
        .strength(0.8)
      )
      .force('center', d3.forceCenter(centerX, centerY).strength(0.05))
      .alphaDecay(0.05)
      .velocityDecay(0.8)
      .stop();

    for (let i = 0; i < 150; ++i) {
      simulation.tick();
    }

    const bubbleGroups = svg.selectAll('.bubble-group')
      .data(dataArray)
      .enter()
      .append('g')
      .attr('class', 'bubble-group')
      .style('cursor', type !== 'stock' && isInteractive ? 'pointer' : 'default')
      .style('pointer-events', isInteractive ? 'all' : 'none');

    const circles = bubbleGroups.append('circle')
      .attr('r', 0)
      .attr('fill', (d, i) => {
        if (type === 'stock') {
          return getChangeColor(d.change || 0);
        } else {
          return `url(#gradient-${i})`;
        }
      })
      .attr('stroke', '#fff')
      .attr('stroke-width', 2)
      .style('opacity', 0.8);

    if (type !== 'stock') {
      const defsLocal = svg.append('defs');

      dataArray.forEach((d, i) => {
        const baseColor = getChangeColor(d.aggregateChange);
        const colorObj = d3.color(baseColor) || d3.rgb('#888888');
        const inner = colorObj.brighter(0.5).formatHex();
        const outer = colorObj.darker(0.5).formatHex();

        const gradient = defsLocal.append('radialGradient')
          .attr('id', `gradient-${i}`)
          .attr('cx', '50%')
          .attr('cy', '50%')
          .attr('r', '50%');

        gradient.append('stop')
          .attr('offset', '0%')
          .attr('stop-color', inner)
          .attr('stop-opacity', 1);

        gradient.append('stop')
          .attr('offset', '100%')
          .attr('stop-color', outer)
          .attr('stop-opacity', 0.6);
      });
    }

    const defs = svg.select('defs').empty() ? svg.append('defs') : svg.select('defs');
    const filter = defs.append('filter')
      .attr('id', 'glow');

    filter.append('feGaussianBlur')
      .attr('stdDeviation', '3')
      .attr('result', 'coloredBlur');

    const feMerge = filter.append('feMerge');
    feMerge.append('feMergeNode').attr('in', 'coloredBlur');
    feMerge.append('feMergeNode').attr('in', 'SourceGraphic');

    bubbleGroups.append('text')
      .attr('text-anchor', 'middle')
      .attr('dy', type === 'stock' ? '-0.2em' : '0.35em')
      .style('font-family', 'Arial, sans-serif')
      .style('font-weight', 'bold')
      .style('font-size', type === 'stock' ? '14px' : '16px')
      .style('fill', '#ffffff')
      .style('pointer-events', 'none')
      .style('opacity', 0)
      .text(d => d.name);

    if (type === 'stock') {
      bubbleGroups.append('text')
        .attr('text-anchor', 'middle')
        .attr('dy', '1em')
        .style('font-family', 'Arial, sans-serif')
        .style('font-size', '12px')
        .style('fill', '#ffffff')
        .style('pointer-events', 'none')
        .style('opacity', 0)
        .text(d => `$${d.price}`);

      bubbleGroups.append('text')
        .attr('text-anchor', 'middle')
        .attr('dy', '2.2em')
        .style('font-family', 'Arial, sans-serif')
        .style('font-size', '11px')
        .style('fill', '#ffffff')
        .style('pointer-events', 'none')
        .style('opacity', 0)
        .text(d => `${d.change >= 0 ? '+' : ''}${d.change.toFixed(1)}%`);
    }

    bubbleGroups
      .on('mouseenter', function(event, d) {
        if (type !== 'stock') {
          d3.select(this).select('circle')
            .transition()
            .duration(200)
            .attr('r', d.radius * 1.1)
            .style('filter', 'url(#glow)');
        }
      })
      .on('mousemove', function(event, d) {
        if (type === 'stock') {
          const [mouseX, mouseY] = d3.pointer(event, document.body);
          setTooltip({
            visible: true,
            x: mouseX + 10,
            y: mouseY - 10,
            data: d
          });
        }
      })
      .on('mouseleave', function(event, d) {
        setTooltip(prev => ({ ...prev, visible: false }));
        d3.select(this).select('circle')
          .transition()
          .duration(200)
          .attr('r', d.radius)
          .style('filter', 'none');
      });

    bubbleGroups.attr('transform', d => `translate(${d.x},${d.y})`);

    circles.transition()
      .duration(800)
      .delay((d, i) => i * 100)
      .attr('r', d => d.radius)
      .ease(d3.easeElasticOut);

    bubbleGroups.selectAll('text')
      .transition()
      .duration(500)
      .delay((d, i) => i * 100 + 400)
      .style('opacity', 1);

    if (isInteractive && type === 'sector') {
      bubbleGroups.on('click', (event, d) => {
        setSelectedSector(d);
        setCurrentLevel('industry');
        setBreadcrumb(['Stock Market', d.name]);
      });
    } else if (isInteractive && type === 'industry') {
      bubbleGroups.on('click', (event, d) => {
        setSelectedIndustry(d);
        setCurrentLevel('stock');
        setBreadcrumb(['Stock Market', (selectedSector && selectedSector.name) || '', d.name]);
      });
    } else if (isInteractive && type === 'stock') {
      bubbleGroups.on('click', (event, d) => {
        addToPortfolio(d);
      });
    }
  }, [addToPortfolio, isInteractive, selectedSector]);

  useEffect(() => {
    loadMarketData();
  }, [loadMarketData]);

  useEffect(() => {
    if (!sectorData.length) return;

    if (currentLevel === 'sector') {
      drawBubbles(sectorData, 'sector');
    } else if (currentLevel === 'industry' && selectedSector) {
      drawBubbles(selectedSector.industries, 'industry');
    } else if (currentLevel === 'stock' && selectedIndustry) {
      drawBubbles(selectedIndustry.stocks, 'stock');
    }
  }, [currentLevel, selectedSector, selectedIndustry, sectorData, drawBubbles]);

  // Load the user's last saved portfolio from Firestore on login
  useEffect(() => {
    const loadPortfolio = async () => {
      if (!user?.uid || hasLoadedFromDb.current) return;
      try {
        const snapshot = await getDoc(doc(db, 'users', user.uid));
        const data = snapshot.exists() ? snapshot.data() : null;
        if (data?.lastPortfolio?.items && Array.isArray(data.lastPortfolio.items)) {
          setPortfolio(data.lastPortfolio.items);
        }
      } catch (err) {
        console.warn('Could not load saved portfolio:', err);
      } finally {
        hasLoadedFromDb.current = true;
      }
    };
    loadPortfolio();
  }, [user]);

  const handleBreadcrumbClick = (index) => {
    if (index === 0) {
      setCurrentLevel('sector');
      setSelectedSector(null);
      setSelectedIndustry(null);
      setBreadcrumb(['Stock Market']);
    } else if (index === 1) {
      setCurrentLevel('industry');
      setSelectedIndustry(null);
      setBreadcrumb(['Stock Market', (selectedSector && selectedSector.name) || '']);
    }
  };

  const updateStockQuantity = (stockName, change) => {
    setPortfolio(prev => {
      return prev.map(stock => {
        if (stock.name === stockName) {
          const newQuantity = Math.max(0, stock.quantity + change);
          return newQuantity === 0 ? null : { ...stock, quantity: newQuantity };
        }
        return stock;
      }).filter(Boolean);
    });
  };

  const getTotalValue = () => {
    return portfolio.reduce((total, stock) => total + (stock.price * stock.quantity), 0);
  };

  const savePortfolioOnly = async () => {
    if (portfolio.length === 0) {
      setExportStatus('No stocks in portfolio to save.');
      setTimeout(() => setExportStatus(''), 3000);
      return;
    }

    setExportStatus('Saving portfolio...');

    try {
      if (!user?.uid) {
        throw new Error('User is not authenticated.');
      }

      await setDoc(
        doc(db, 'users', user.uid),
        {
          lastPortfolio: {
            items: portfolio,
            totalValue: getTotalValue(),
            averageChange: getTotalChange(),
            analyzedAt: serverTimestamp(),
          },
          profile: {
            displayName: user.displayName || '',
            email: user.email || '',
            photoURL: user.photoURL || '',
          },
        },
        { merge: true }
      );

      setExportStatus('Portfolio saved.');
      setTimeout(() => setExportStatus(''), 3000);
    } catch (error) {
      console.error('Save failed:', error);
      setExportStatus(error.message || 'Save failed. Please try again.');
      setTimeout(() => setExportStatus(''), 3000);
    }
  };

  const exportPortfolio = async () => {
    if (portfolio.length === 0) {
      setExportStatus('No stocks in portfolio to export!');
      setTimeout(() => setExportStatus(''), 3000);
      return;
    }

    setExportStatus('Saving portfolio...');

    const tickersList = portfolio.map(s => s.name).join(', ');
    const header = `All Stocks: ${tickersList};`;
    const perStockDetails = portfolio.map(stock =>
      `${stock.name}: ${stock.quantity} shares @ ${stock.price} (${stock.change >= 0 ? '+' : ''}${stock.change.toFixed(1)}%)`
    ).join('; ');
    const portfolioSummary = `${header}; ${perStockDetails}`;

    try {
      // Write the analyzed portfolio to Firestore for this user
      if (!user?.uid) {
        throw new Error('User is not authenticated.')
      }

      await setDoc(
        doc(db, 'users', user.uid),
        {
          lastPortfolio: {
            summary: portfolioSummary,
            items: portfolio,
            totalValue: getTotalValue(),
            averageChange: getTotalChange(),
            analyzedAt: serverTimestamp(),
          },
          profile: {
            displayName: user.displayName || '',
            email: user.email || '',
            photoURL: user.photoURL || '',
          },
        },
        { merge: true }
      )

      // Keep legacy server hook if available
      try {
        await apiFetch('/save-portfolio', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            message: `${portfolioSummary}, Portfolio Export - Total Value: ${getTotalValue().toLocaleString()}, Avg Change: ${getTotalChange().toFixed(1)}%`
          })
        })
      } catch (serverErr) {
        console.warn('Optional server sync failed, continuing with Firestore only.', serverErr)
      }

      setExportStatus('Portfolio saved to your account! Redirecting...');
      
      // Navigate to output page after successful save
      setTimeout(() => {
        if (onNavigate) {
          onNavigate('output');
        }
      }, 800);
    } catch (error) {
      console.error('Export failed:', error);
      setExportStatus(error.message || 'Saving failed. Please try again later.');
      setTimeout(() => setExportStatus(''), 3000);
    }
  };

  const getTotalChange = () => {
    if (portfolio.length === 0) return 0;
    const weightedChange = portfolio.reduce((total, stock) => {
      return total + (stock.change * stock.quantity * stock.price);
    }, 0);
    const totalValue = getTotalValue();
    return totalValue > 0 ? (weightedChange / totalValue) : 0;
  };

  return (
    <div className="min-h-screen bg-white text-slate-900">
      {/* Navigation */}
      <nav className="relative z-10 px-6 py-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <button 
            onClick={() => onNavigate && onNavigate('home')} 
            className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-700 via-fuchsia-600 to-purple-500 flex items-center gap-2"
          >
            <Home className="w-6 h-6" />
            Neo
          </button>
          <div className="flex items-center gap-3">
            {user && (
              <div className="hidden sm:flex items-center gap-2 bg-white border border-purple-100 rounded-full px-3 py-1 shadow-sm">
                {user.photoURL ? (
                  <img src={user.photoURL} alt={user.displayName || 'User'} className="w-8 h-8 rounded-full border border-purple-200" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-fuchsia-500 text-white font-bold flex items-center justify-center">
                    {userInitial}
                  </div>
                )}
                <div>
                  <p className="text-xs text-purple-500 leading-none">Signed in</p>
                  <p className="text-sm text-slate-900 font-semibold leading-none truncate max-w-[140px]">{user.displayName || user.email}</p>
                </div>
              </div>
            )}
            <button
              onClick={() => onNavigate && onNavigate('portfolio')}
              className="bg-gradient-to-r from-purple-600 to-fuchsia-600 text-white px-4 py-2 rounded-lg hover:shadow-lg hover:shadow-purple-200 transition"
            >
              Portfolio
            </button>
            <button 
              onClick={() => onNavigate && onNavigate('home')} 
              className="text-purple-600 hover:text-purple-800 transition-colors"
            >
              ← Back to Home
            </button>
            {onLogout && (
              <button
                onClick={onLogout}
                className="flex items-center gap-2 bg-white border border-purple-100 text-purple-700 px-4 py-2 rounded-lg hover:shadow-md transition"
              >
                <LogOut className="w-4 h-4" />
                Log out
              </button>
            )}
          </div>
        </div>
      </nav>

      <div className="flex p-6 text-slate-900 gap-4">
        <div className="flex-1 flex flex-col items-center">
          {/* Tooltip */}
          {tooltip.visible && tooltip.data && (
            <div
              className="fixed z-50 bg-white border border-purple-100 rounded-lg p-3 shadow-xl pointer-events-none"
              style={{
                left: tooltip.x,
                top: tooltip.y,
                transform: 'translate(-50%, -100%)'
              }}
            >
              <div className="text-sm">
                <div className="font-bold text-slate-900 mb-1">{tooltip.data.name}</div>
                <div className="text-slate-700">Price: ${tooltip.data.price}</div>
                <div className={`text-sm ${tooltip.data.change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  Change: {tooltip.data.change >= 0 ? '+' : ''}{tooltip.data.change.toFixed(1)}%
                </div>
                {tooltip.data.marketCap && (
                  <div className="text-slate-500 text-xs">
                    Market Cap: ${(tooltip.data.marketCap / 1000).toFixed(0)}B
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="mb-6 flex flex-col items-center gap-4">
            <nav className="flex items-center space-x-2 text-sm">
              {breadcrumb.map((crumb, index) => (
                <React.Fragment key={index}>
                  <button
                    onClick={() => handleBreadcrumbClick(index)}
                    className={`px-3 py-1 rounded ${
                      index === breadcrumb.length - 1
                        ? 'bg-gradient-to-r from-purple-600 to-fuchsia-600 text-white shadow-lg shadow-purple-200'
                        : 'bg-purple-50 text-purple-700 hover:bg-purple-100 border border-purple-100'
                    } transition-colors duration-200`}
                  >
                    {crumb}
                  </button>
                  {index < breadcrumb.length - 1 && (
                    <span className="text-purple-400">/</span>
                  )}
                </React.Fragment>
              ))}
            </nav>

            <div className="flex items-center gap-3">
              <button
                onClick={() => loadMarketData(true)}
                disabled={isLoadingData}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                  isLoadingData
                    ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-purple-600 to-fuchsia-600 text-white shadow-sm hover:shadow-md'
                }`}
              >
                {isLoadingData ? 'Updating…' : 'Refresh live data'}
              </button>
              {dataStatus && (
                <span className="text-xs text-purple-700 bg-purple-50 border border-purple-100 px-3 py-2 rounded-lg">
                  {dataStatus}
                </span>
              )}
            </div>

            {exportStatus && (
              <div className={`p-3 rounded-lg text-sm font-medium ${
                exportStatus.includes('Success') ? 'bg-green-50 text-green-700 border border-green-200' :
                exportStatus.includes('unavailable') ? 'bg-amber-50 text-amber-700 border border-amber-200' :
                'bg-blue-50 text-blue-700 border border-blue-200'
              }`}>
                {exportStatus}
              </div>
            )}

            <button
              onClick={exportPortfolio}
              disabled={portfolio.length === 0}
              className={`px-6 py-3 rounded-lg font-semibold text-sm transition-all transform hover:scale-105 flex items-center justify-center gap-2 ${
                portfolio.length === 0 
                  ? 'bg-slate-100 text-slate-400 cursor-not-allowed' 
                  : 'bg-gradient-to-r from-purple-600 to-fuchsia-600 hover:from-purple-700 hover:to-fuchsia-700 text-white shadow-lg shadow-purple-200'
              }`}
            >
              Analyze Portfolio ({portfolio.length} stocks)
            </button>
          </div>

          <div className="bg-white rounded-lg shadow-xl border border-purple-100">
            <svg ref={svgRef} className="block"></svg>
          </div>

          <div className="mt-4 text-center">
            <p className="text-purple-700 text-sm">
              {currentLevel === 'sector' && 'Click on a sector to explore its industries'}
              {currentLevel === 'industry' && 'Click on an industry to see its stocks'}
              {currentLevel === 'stock' && 'Click on stocks to add them to your portfolio - Green: gain, Red: loss'}
            </p>
          </div>
        </div>

        {/* Portfolio Sidebar */}
        <div className="w-72 bg-white rounded-lg shadow-xl border border-purple-100 p-6 flex flex-col gap-4">
          <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-purple-500" />
            My Portfolio
          </h2>

          <div className="flex-1 flex flex-col gap-4">
            {portfolio.length === 0 ? (
              <p className="text-slate-600 text-sm">Click on stocks to add them to your portfolio</p>
            ) : (
              <>
                <div className="p-3 bg-purple-50 rounded-lg border border-purple-100">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-purple-700">Total Value</span>
                    <span className="text-lg font-bold text-slate-900">${getTotalValue().toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-purple-700">Avg Change</span>
                    <span className={`text-sm font-semibold flex items-center gap-1 ${
                      getTotalChange() >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {getTotalChange() >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                      {getTotalChange() >= 0 ? '+' : ''}{getTotalChange().toFixed(1)}%
                    </span>
                  </div>
                </div>

                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {portfolio.map((stock) => (
                    <div key={stock.id} className="p-3 bg-white rounded-lg border border-purple-100 hover:border-purple-200 transition-colors shadow-sm">
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold text-slate-900">{stock.name}</span>
                          <span className="text-xs text-slate-500">×{stock.quantity}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-slate-700">${stock.price} each</span>
                          <span className={`flex items-center gap-1 ${
                            stock.change >= 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {stock.change >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                            {stock.change >= 0 ? '+' : ''}{stock.change.toFixed(1)}%
                          </span>
                        </div>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-xs text-slate-500">
                            Total: ${(stock.price * stock.quantity).toLocaleString()}
                          </span>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => updateStockQuantity(stock.name, -1)}
                              className="w-6 h-6 flex items-center justify-center bg-red-50 hover:bg-red-100 text-red-600 rounded text-xs font-bold transition-colors"
                            >
                              -
                            </button>
                            <button
                              onClick={() => updateStockQuantity(stock.name, 1)}
                              className="w-6 h-6 flex items-center justify-center bg-green-50 hover:bg-green-100 text-green-600 rounded text-xs font-bold transition-colors"
                            >
                              +
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          <button
            onClick={savePortfolioOnly}
            disabled={portfolio.length === 0}
            className={`w-full py-4 px-4 rounded-xl font-semibold text-sm transition-all ${
              portfolio.length === 0
                ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                : 'bg-gradient-to-r from-purple-600 to-fuchsia-600 hover:from-purple-700 hover:to-fuchsia-700 text-white shadow-lg shadow-purple-200'
            }`}
          >
            Save Portfolio
          </button>
        </div>
      </div>
    </div>
  );
};

export default BubbleChart;
